// eslint-disable-next-line import/no-duplicates
// need a separate import statement for the locale, but eslint complains
// eslint-disable-next-line import/no-duplicates
import { getLanguageFromLanguageWithLocale, SUPPORTED_LANGUAGES } from '@eversports/language'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { MobileDatePicker, MobileDatePickerProps } from '@mui/x-date-pickers/MobileDatePicker'
import { format, Locale } from 'date-fns'
import enLocale from 'date-fns/locale/en-GB/index.js'
import { PyroFormValues, usePyroField } from 'pyro-form'
import { useEffect, useState } from 'react'
import * as React from 'react'

import { Locales } from '../../localization'
import TextField from '../TextField'

type DatePickerRef = HTMLInputElement

type PickedMuiDatePickerProps = Pick<
  MobileDatePickerProps<Date, Date>,
  'minDate' | 'maxDate' | 'label' | 'disableFuture'
> & {
  initialFocusedDate: Date
}
export interface DatePickerProps<Values extends PyroFormValues> extends Partial<PickedMuiDatePickerProps> {
  name: Extract<keyof Values, string>
  language?: Locales
  ref?: React.Ref<DatePickerRef>
  'data-testid'?: string
}

// If the user's browser language isn't supported, return English
// Note: English will point to British English - later region should be supported
const getLanguage = (): Locales => {
  const locale = getLanguageFromLanguageWithLocale(navigator.language)
  return SUPPORTED_LANGUAGES.has(locale) ? locale : 'en'
}

const DatePicker = React.forwardRef(function DatePicker<Values extends PyroFormValues = any>(
  { name, label, initialFocusedDate, 'data-testid': dataTestid, ...muiProps }: DatePickerProps<Values>,
  ref?: React.Ref<DatePickerRef>,
) {
  const [date, setDate] = useState<Date | null>(initialFocusedDate ? new Date(initialFocusedDate) : new Date())
  const [locale, setLocale] = useState<Locale>(enLocale)
  const { core } = usePyroField<Values, Extract<keyof Values, string>>(name)

  useEffect(() => {
    const importLocale = async (language: Locales) => {
      try {
        const localeToSet: Locale = await import(
          /* webpackExclude: /_lib/ */
          /* webpackChunkName: "df-[index]" */
          /* webpackMode: "lazy" */
          `date-fns/locale/${language === 'en' ? 'en-GB' : language}/index.js`
        )

        if (localeToSet) {
          setLocale(localeToSet)
        }
      } catch (e: any) {
        return
      }
    }

    const language = getLanguage()

    if (language !== 'en') {
      void importLocale(language)
    }
  }, [])

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
      <MobileDatePicker
        {...muiProps}
        inputRef={ref}
        label={label}
        value={date}
        onChange={setDate}
        onAccept={(newDate: Date | null) => {
          if (newDate) {
            core.onChange(format(newDate, 'yyyy-MM-dd'))
          }
        }}
        renderInput={(params) => <TextField data-testid={dataTestid} {...params} variant="outlined" name={name} />}
      />
    </LocalizationProvider>
  )
})

export default DatePicker
